<template>
  <div class="service-status-container">
    <!-- Compact Status Display -->
    <div v-if="!expanded" class="compact-status">
      <button
        @click="toggleExpanded"
        class="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
        :class="getOverallStatusClass()"
        :title="getStatusTooltip()"
      >
        <!-- Overall Status Indicator -->
        <div class="relative">
          <div 
            class="w-3 h-3 rounded-full transition-all duration-300"
            :class="getOverallStatusDotClass()"
          ></div>
          <div 
            v-if="isOverallOnline"
            class="absolute inset-0 w-3 h-3 rounded-full animate-ping"
            :class="getOverallStatusDotClass()"
          ></div>
        </div>
        
        <!-- Status Text -->
        <span class="text-sm font-medium">
          {{ getOverallStatusText() }}
        </span>
        
        <!-- Service Count -->
        <span class="text-xs opacity-75">
          ({{ onlineServices }}/{{ totalServices }})
        </span>
        
        <!-- Expand Icon -->
        <ChevronDownIcon 
          class="w-4 h-4 transition-transform duration-200"
          :class="{ 'rotate-180': expanded }"
        />
      </button>
    </div>

    <!-- Expanded Status Display -->
    <div v-if="expanded" class="expanded-status">
      <div class="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-4 min-w-80">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-2">
            <div 
              class="w-4 h-4 rounded-full"
              :class="getOverallStatusDotClass()"
            ></div>
            <h3 class="text-lg font-semibold text-gray-900">Service Status</h3>
          </div>
          
          <div class="flex items-center space-x-2">
            <!-- Auto-refresh toggle -->
            <button
              @click="toggleAutoRefresh"
              class="p-1 rounded-md hover:bg-gray-100 transition-colors"
              :class="{ 'text-blue-600': autoRefresh, 'text-gray-400': !autoRefresh }"
              :title="autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'"
            >
              <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': isRefreshing }" />
            </button>
            
            <!-- Close button -->
            <button
              @click="toggleExpanded"
              class="p-1 rounded-md hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Service List -->
        <div class="space-y-3">
          <!-- Frontend Service -->
          <div class="service-item">
            <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div class="flex items-center space-x-3">
                <div class="relative">
                  <div 
                    class="w-3 h-3 rounded-full transition-all duration-300"
                    :class="getServiceStatusClass(status?.frontend)"
                  ></div>
                  <div 
                    v-if="status?.frontend?.available"
                    class="absolute inset-0 w-3 h-3 rounded-full animate-ping"
                    :class="getServiceStatusClass(status?.frontend)"
                  ></div>
                </div>
                
                <div>
                  <div class="flex items-center space-x-2">
                    <BoltIcon class="w-4 h-4 text-emerald-600" />
                    <span class="font-medium text-gray-900">Frontend Service</span>
                  </div>
                  <p class="text-sm text-gray-600 mt-1">
                    {{ getServiceDescription('frontend') }}
                  </p>
                </div>
              </div>
              
              <div class="text-right">
                <div class="flex items-center space-x-2">
                  <span 
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="getServiceBadgeClass(status?.frontend)"
                  >
                    {{ getServiceStatusText(status?.frontend) }}
                  </span>
                </div>
                
                <!-- Response Time -->
                <div v-if="status?.frontend?.responseTime" class="text-xs text-gray-500 mt-1">
                  {{ status.frontend.responseTime }}ms
                </div>
              </div>
            </div>
            
            <!-- Frontend Capabilities -->
            <div v-if="status?.frontend?.available && status?.frontend?.capabilities" 
                 class="mt-2 ml-6">
              <div class="text-xs text-gray-600 mb-1">Capabilities:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="capability in status.frontend.capabilities"
                  :key="capability"
                  class="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs"
                >
                  {{ capability }}
                </span>
              </div>
            </div>
          </div>

          <!-- Backend Service -->
          <div class="service-item">
            <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div class="flex items-center space-x-3">
                <div class="relative">
                  <div 
                    class="w-3 h-3 rounded-full transition-all duration-300"
                    :class="getServiceStatusClass(status?.backend)"
                  ></div>
                  <div 
                    v-if="status?.backend?.available"
                    class="absolute inset-0 w-3 h-3 rounded-full animate-ping"
                    :class="getServiceStatusClass(status?.backend)"
                  ></div>
                </div>
                
                <div>
                  <div class="flex items-center space-x-2">
                    <ServerIcon class="w-4 h-4 text-blue-600" />
                    <span class="font-medium text-gray-900">Backend Service</span>
                  </div>
                  <p class="text-sm text-gray-600 mt-1">
                    {{ getServiceDescription('backend') }}
                  </p>
                </div>
              </div>
              
              <div class="text-right">
                <div class="flex items-center space-x-2">
                  <span 
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="getServiceBadgeClass(status?.backend)"
                  >
                    {{ getServiceStatusText(status?.backend) }}
                  </span>
                </div>
                
                <!-- Response Time -->
                <div v-if="status?.backend?.responseTime" class="text-xs text-gray-500 mt-1">
                  {{ status.backend.responseTime }}ms
                </div>
              </div>
            </div>
            
            <!-- Backend Features -->
            <div v-if="status?.backend?.available && status?.backend?.features" 
                 class="mt-2 ml-6">
              <div class="text-xs text-gray-600 mb-1">Features:</div>
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="feature in status.backend.features"
                  :key="feature"
                  class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                >
                  {{ feature }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Last Updated -->
        <div class="mt-4 pt-3 border-t border-gray-200">
          <div class="flex items-center justify-between text-xs text-gray-500">
            <span>Last updated: {{ formatLastUpdated() }}</span>
            <button
              @click="refreshStatus"
              :disabled="isRefreshing"
              class="inline-flex items-center space-x-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon class="w-3 h-3" :class="{ 'animate-spin': isRefreshing }" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <!-- Service Recommendations -->
        <div v-if="hasRecommendations" class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div class="flex items-start space-x-2">
            <ExclamationTriangleIcon class="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div class="text-sm">
              <div class="font-medium text-amber-800 mb-1">Recommendations</div>
              <ul class="text-amber-700 space-y-1">
                <li v-for="recommendation in getRecommendations()" :key="recommendation">
                  {{ recommendation }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import {
  ChevronDownIcon,
  XMarkIcon,
  ArrowPathIcon,
  BoltIcon,
  ServerIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { formatDistanceToNow } from 'date-fns'

export default {
  name: 'ServiceStatus',
  components: {
    ChevronDownIcon,
    XMarkIcon,
    ArrowPathIcon,
    BoltIcon,
    ServerIcon,
    ExclamationTriangleIcon
  },
  props: {
    status: {
      type: Object,
      default: () => ({
        frontend: { available: false },
        backend: { available: false }
      })
    },
    autoRefreshInterval: {
      type: Number,
      default: 30000 // 30 seconds
    },
    showExpanded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'status-change'],
  setup(props, { emit }) {
    const expanded = ref(props.showExpanded)
    const autoRefresh = ref(true)
    const isRefreshing = ref(false)
    const lastUpdated = ref(new Date())
    const refreshInterval = ref(null)

    // Computed properties
    const totalServices = computed(() => 2)
    
    const onlineServices = computed(() => {
      let count = 0
      if (props.status?.frontend?.available) count++
      if (props.status?.backend?.available) count++
      return count
    })

    const isOverallOnline = computed(() => onlineServices.value > 0)

    const hasRecommendations = computed(() => {
      return !props.status?.frontend?.available || !props.status?.backend?.available
    })

    // Methods
    const toggleExpanded = () => {
      expanded.value = !expanded.value
    }

    const toggleAutoRefresh = () => {
      autoRefresh.value = !autoRefresh.value
      if (autoRefresh.value) {
        startAutoRefresh()
      } else {
        stopAutoRefresh()
      }
    }

    const refreshStatus = async () => {
      if (isRefreshing.value) return
      
      isRefreshing.value = true
      try {
        await emit('refresh')
        lastUpdated.value = new Date()
      } finally {
        setTimeout(() => {
          isRefreshing.value = false
        }, 500) // Show spinner for at least 500ms
      }
    }

    const startAutoRefresh = () => {
      if (refreshInterval.value) return
      
      refreshInterval.value = setInterval(async () => {
        if (autoRefresh.value) {
          await refreshStatus()
        }
      }, props.autoRefreshInterval)
    }

    const stopAutoRefresh = () => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value)
        refreshInterval.value = null
      }
    }

    const getOverallStatusClass = () => {
      if (onlineServices.value === totalServices.value) {
        return 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
      } else if (onlineServices.value > 0) {
        return 'bg-amber-500/20 text-amber-100 border border-amber-400/30'
      } else {
        return 'bg-red-500/20 text-red-100 border border-red-400/30'
      }
    }

    const getOverallStatusDotClass = () => {
      if (onlineServices.value === totalServices.value) {
        return 'bg-emerald-400'
      } else if (onlineServices.value > 0) {
        return 'bg-amber-400'
      } else {
        return 'bg-red-400'
      }
    }

    const getOverallStatusText = () => {
      if (onlineServices.value === totalServices.value) {
        return 'All Systems Operational'
      } else if (onlineServices.value > 0) {
        return 'Partial Outage'
      } else {
        return 'System Unavailable'
      }
    }

    const getStatusTooltip = () => {
      const frontendStatus = props.status?.frontend?.available ? 'Online' : 'Offline'
      const backendStatus = props.status?.backend?.available ? 'Online' : 'Offline'
      return `Frontend: ${frontendStatus}, Backend: ${backendStatus}`
    }

    const getServiceStatusClass = (service) => {
      if (!service) return 'bg-gray-400'
      
      if (service.available) {
        return 'bg-emerald-400'
      } else if (service.degraded) {
        return 'bg-amber-400'
      } else {
        return 'bg-red-400'
      }
    }

    const getServiceBadgeClass = (service) => {
      if (!service) return 'bg-gray-100 text-gray-800'
      
      if (service.available) {
        return 'bg-emerald-100 text-emerald-800'
      } else if (service.degraded) {
        return 'bg-amber-100 text-amber-800'
      } else {
        return 'bg-red-100 text-red-800'
      }
    }

    const getServiceStatusText = (service) => {
      if (!service) return 'Unknown'
      
      if (service.available) {
        return 'Online'
      } else if (service.degraded) {
        return 'Degraded'
      } else {
        return 'Offline'
      }
    }

    const getServiceDescription = (type) => {
      const descriptions = {
        frontend: 'Client-side QR generation and styling',
        backend: 'Server-side processing and advanced features'
      }
      return descriptions[type] || 'Service description unavailable'
    }

    const getRecommendations = () => {
      const recommendations = []
      
      if (!props.status?.frontend?.available && !props.status?.backend?.available) {
        recommendations.push('Both services are offline. Please check your internet connection.')
      } else if (!props.status?.frontend?.available) {
        recommendations.push('Frontend service offline. Using backend for all operations.')
        recommendations.push('Some styling features may be limited.')
      } else if (!props.status?.backend?.available) {
        recommendations.push('Backend service offline. Logo/icon features unavailable.')
        recommendations.push('Advanced QR options may not work.')
      }
      
      return recommendations
    }

    const formatLastUpdated = () => {
      return formatDistanceToNow(lastUpdated.value, { addSuffix: true })
    }

    // Watch for status changes
    watch(() => props.status, (newStatus, oldStatus) => {
      if (JSON.stringify(newStatus) !== JSON.stringify(oldStatus)) {
        emit('status-change', newStatus)
        lastUpdated.value = new Date()
      }
    }, { deep: true })

    // Lifecycle
    onMounted(() => {
      if (autoRefresh.value) {
        startAutoRefresh()
      }
    })

    onUnmounted(() => {
      stopAutoRefresh()
    })

    return {
      expanded,
      autoRefresh,
      isRefreshing,
      lastUpdated,
      totalServices,
      onlineServices,
      isOverallOnline,
      hasRecommendations,
      toggleExpanded,
      toggleAutoRefresh,
      refreshStatus,
      getOverallStatusClass,
      getOverallStatusDotClass,
      getOverallStatusText,
      getStatusTooltip,
      getServiceStatusClass,
      getServiceBadgeClass,
      getServiceStatusText,
      getServiceDescription,
      getRecommendations,
      formatLastUpdated
    }
  }
}
</script>

<style scoped>
.service-status-container {
  position: relative;
}

.compact-status {
  display: inline-block;
}

.expanded-status {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 50;
  margin-top: 0.5rem;
}

.service-item {
  transition: all 0.2s ease;
}

.service-item:hover {
  transform: translateY(-1px);
}

/* Animation for ping effect */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.transition-transform {
  transition-property: transform;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Focus styles */
.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.focus\:ring-2:focus {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
}

/* Hover effects */
.hover\:bg-white\/10:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}

.hover\:text-gray-600:hover {
  color: #4b5563;
}

/* Disabled styles */
.disabled\:opacity-50:disabled {
  opacity: 0.5;
}

/* Responsive design */
@media (max-width: 640px) {
  .expanded-status {
    right: auto;
    left: 0;
    min-width: 100vw;
    padding: 0 1rem;
  }
  
  .min-w-80 {
    min-width: 100%;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .bg-white\/95 {
    background-color: rgba(17, 24, 39, 0.95);
  }
  
  .text-gray-900 {
    color: #f9fafb;
  }
  
  .text-gray-600 {
    color: #9ca3af;
  }
  
  .text-gray-500 {
    color: #6b7280;
  }
  
  .border-gray-200 {
    border-color: #374151;
  }
  
  .bg-gray-50 {
    background-color: #111827;
  }
  
  .hover\:bg-gray-100:hover {
    background-color: #1f2937;
  }
}
</style>